/**
 * Custom error types for Intiface remote connection failures.
 * Provides specific error classes for different failure scenarios to enable
 * better error handling and reporting.
 */

/**
 * Base class for all Intiface-related errors.
 */
export class IntifaceError extends Error {
    constructor(message: string, public readonly code: string) {
        super(message);
        this.name = this.constructor.name;
        // Maintains proper stack trace for where error was thrown (V8 only)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }
}

/**
 * Thrown when WebRTC connection establishment fails or times out.
 */
export class ConnectionError extends IntifaceError {
    constructor(message: string, public readonly reason?: string) {
        super(message, 'CONNECTION_ERROR');
    }
}

/**
 * Thrown when WebRTC ICE candidate negotiation fails.
 */
export class ICEError extends IntifaceError {
    constructor(message: string, public readonly candidate?: RTCIceCandidateInit) {
        super(message, 'ICE_ERROR');
    }
}

/**
 * Thrown when RPC method invocation fails or times out.
 */
export class RPCError extends IntifaceError {
    constructor(
        message: string,
        public readonly method?: string,
        public readonly params?: any
    ) {
        super(message, 'RPC_ERROR');
    }
}

/**
 * Thrown when attempting to control devices that are unavailable or not found.
 */
export class DeviceNotFoundError extends IntifaceError {
    constructor(
        message: string,
        public readonly deviceIndex?: number,
        public readonly peerId?: number
    ) {
        super(message, 'DEVICE_NOT_FOUND');
    }
}

/**
 * Thrown when data channel operations fail.
 */
export class DataChannelError extends IntifaceError {
    constructor(message: string, public readonly channelState?: RTCDataChannelState) {
        super(message, 'DATACHANNEL_ERROR');
    }
}

/**
 * Thrown when attempting operations on a disconnected peer.
 */
export class PeerDisconnectedError extends IntifaceError {
    constructor(message: string, public readonly peerId?: number) {
        super(message, 'PEER_DISCONNECTED');
    }
}
